import { Link } from "react-router-dom";
import { isEmpty, startCase, isEqual, get } from "lodash";
import { format, isBefore, addDays } from "date-fns";
import { useSelector, useDispatch } from "react-redux";
import React, { useState, useEffect, useCallback } from "react";
import { Grid, Menu, Message, Confirm, Modal, Table, Button } from "semantic-ui-react";
import Swal from "sweetalert2";

import TopNav from "../../common/containers/top-nav";
import InvoiceComponentView from "../components/view";
import { statusMapping } from "../components/status";
import { DateTimeFormat, InvoiceStatuses } from "utilities/dist/invoice/constants";
import { DateFormat, generatePdf, generatePdfLocations } from "../helpers";
import { makePrivateApiCall } from "utilities/dist/api";
import { saveInvoiceBuilderInvoice, updateInvoiceStatus } from "../builder/actions";
import { setInvoiceNotes } from "../document-upload/actions";
import InvoicePaymentListContainer from "../payment/list.container";
import {
    EmailFeatureDetails,
    isFeatureActive,
    PaymentFeatureDetails,
    FintechFeature,
} from "../../company/feature/helpers";
import DocumentContainer from "../document-upload/document.container";
import { toast } from "react-semantic-toasts";
import InvoiceEmailModal from "../builder/invoice-email.component";
import { buildInvoiceNumberWithBeer } from "utilities/dist/invoice/helpers";
import { isCompanyFeatureActive } from "utilities/dist/company/helpers";
import { CompanyFeatures } from "utilities/dist/company/constants";
import { parseServerTimestampForClient } from "../../common/helpers";
import { FintechComponentSyncHistory } from "../../fintech/components/sync-history";
import OrderNotesComponent from "../../order/components/order-notes";

const isChangeNeeded = ({ field, previousValue, newValue }) => {
    let from = previousValue,
        to = newValue;

    if (field === "invoiced_at") {
        field = "Invoice Date";
        to = format(new Date(newValue), DateFormat);
        from = format(new Date(previousValue), DateFormat);
    }

    if (field === "due_at") {
        field = "Due Date";
        from = !!previousValue ? format(new Date(previousValue), DateFormat) : "Due on delivery";

        to = isBefore(newValue, addDays(new Date(), 1))
            ? "Due on delivery"
            : format(new Date(newValue), DateFormat);
    }

    if (from === to) {
        return false;
    }

    return { from, to };
};

const ConfirmContent = ({ field, previousValue, newValue }) => {
    const changeValues = isChangeNeeded({ field, previousValue, newValue });

    if (!changeValues) {
        return (
            <Modal.Content>
                {field} is not different than the current one, so there's nothing to update.
            </Modal.Content>
        );
    }

    return (
        <Modal.Content>
            Sure you want to change <b>{startCase(field)}</b> from{" "}
            <b>{startCase(changeValues.from)}</b> to <b>{startCase(changeValues.to)}</b>?
        </Modal.Content>
    );
};

const InvoicePageView = ({ computedMatch, history }) => {
    const { number } = computedMatch.params;

    const [generatingPdfUrl, setGeneratingPdfUrl] = useState(false);
    const [generatingPdfLocationsUrl, setGeneratingPdfLocationsUrl] = useState(false);

    const [error, setError] = useState(null);
    const [invoice, setInvoice] = useState({});
    const [isLoading, setLoading] = useState(false);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [isDeleting, setDeleting] = useState(false);
    const [isDeleteConfirming, setDeleteConfirming] = useState(false);

    const [invoiceStatus, setInvoiceStatus] = useState(null);
    const [confirmChange, setConfirmChange] = useState(null);

    const dispatch = useDispatch();
    // see if we already have the data we need from grid, if not, load from server
    const { fromGrid, isSaving, company, user } = useSelector(
        ({ invoiceBuilder, invoiceGrid, auth }) => {
            const isCustomerInvoiceNumberingActive = isCompanyFeatureActive(
                auth.user.company,
                CompanyFeatures.CUSTOMER_INVOICE_NUMBERING
            );
            return {
                fromGrid: invoiceGrid.invoices.find(inv =>
                    isEqual(
                        buildInvoiceNumberWithBeer(inv, isCustomerInvoiceNumberingActive),
                        number
                    )
                ),
                isSaving: invoiceBuilder.isSaving,
                company: auth.user.company,
                user: auth.user,
            };
        }
    );

    if (!!fromGrid && isEmpty(invoice)) {
        setInvoice(fromGrid);
    }
    const canEdit =
        !!invoice &&
        ([
            InvoiceStatuses.DRAFT,
            InvoiceStatuses.READY_FOR_PICK,
            InvoiceStatuses.READY_FOR_TABC_STAMPS,
        ].includes(invoice.status) ||
            (user.role === "owner" &&
                [
                    InvoiceStatuses.INVOICED,
                    InvoiceStatuses.DELIVERED,
                    InvoiceStatuses.PICKED_UP,
                ].includes(invoice?.status)));
    const canDelete =
        !!invoice && [InvoiceStatuses.CANCELLED].includes(invoice.status) && user.role === "owner";
    const hasPaymentFeature = isFeatureActive(company, PaymentFeatureDetails);
    const hasMPowerFeature = isFeatureActive(company, { id: CompanyFeatures.MPOWER });

    const handleDelete = useCallback(() => {
        setDeleting(true);
        makePrivateApiCall({
            url: `/invoice/${number}`,
            method: "delete",
        })
            .then(() => {
                setDeleting(false);
                toast({
                    icon: "trash",
                    type: "success",
                    title: `Invoice deleted`,
                });
                history.push("/invoice");
            })
            .catch(err => {
                setDeleting(false);
                toast({ icon: "trash", type: "error", title: err.message });
            });
    }, [number]);

    const handlePdfGeneration = useCallback(
        ({ onSuccess }) => {
            generatePdf(number, setGeneratingPdfUrl).then(pdfUrl => {
                if (onSuccess) {
                    onSuccess(pdfUrl);
                }
            });
        },
        [number]
    );

    const handlePdfLocationsGeneration = useCallback(
        ({ onSuccess }) => {
            generatePdfLocations(number, setGeneratingPdfLocationsUrl).then(pdfLocationsUrl => {
                if (onSuccess) {
                    onSuccess(pdfLocationsUrl);
                }
            });
        },
        [number]
    );

    const loadInvoice = useCallback(() => {
        makePrivateApiCall({
            url: `/invoice/${number}`,
        })
            .then(res => {
                setLoading(false);
                if (res.data) {
                    setInvoice(res.data);
                    dispatch(setInvoiceNotes(res.data.notes));
                    setError(null);
                }
            })
            .catch(err => {
                setLoading(false);
                setError(err.message);
            });
    }, [number]);

    const onConfirmStatusChange = () => {
        if (!invoiceStatus) return;
        const { newStatus } = invoiceStatus;
        dispatch(updateInvoiceStatus(invoice.id, newStatus))
            .then(() => {
                setInvoice(prev => ({ ...prev, status: newStatus }));
                setInvoiceStatus(null);
                toast({ type: "success", title: "Status updated" });
            })
            .catch(err => {
                setInvoiceStatus(null);
                toast({
                    type: "error",
                    title: get(err, "response.data.message", null) || err.message,
                });
            });
    };

    const handleStatusChangeRequest = value => {
        if (value == null || value === invoice?.status) return;
        setInvoiceStatus({ newStatus: value });
    };

    const saveChange = () => {
        dispatch(
            saveInvoiceBuilderInvoice({
                ...invoice,
                [confirmChange.field]: confirmChange.newValue,
            })
        ).then(inv => {
            setInvoice({ ...invoice, ...inv });
            setConfirmChange(null);
        });
    };

    const handlePaymentChange = () => loadInvoice();

    const syncInvoice = () => {
        let invoiceId = "";

        try {
            const response = JSON.parse(invoice.mPowerInvoice.response);
            invoiceId = response.OrderId;
        } catch (err) {
            invoiceId = invoice.mPowerInvoice.invoice_id;
        }

        if (invoice.mPowerInvoice && !invoice.mPowerInvoice.error) {
            Swal.fire({
                text: `This invoice is already synced to MPower at ${format(
                    parseServerTimestampForClient(invoice.mPowerInvoice.updatedAt),
                    DateFormat
                )}. The sale id is ${invoiceId}. Do you want to re-sync?`,
                icon: "warning",
                iconColor: "#C11428",
                showCancelButton: true,
                confirmButtonColor: "#C11428",
                confirmButtonText: "Yes",
                cancelButtonText: "No",
                cancelButtonClass: "cancelButton",
                reverseButtons: true,
            }).then(result => {
                if (result.isConfirmed) {
                    callSyncApi();
                }
            });
        } else {
            callSyncApi();
        }
    };

    const callSyncApi = () => {
        setLoading(true);
        makePrivateApiCall({
            url: `/mpower/${invoice.id}/sync`,
        })
            .then(() => {
                Swal.fire({
                    icon: "success",
                    text: "Successfully synced invoice.",
                    timer: 1500,
                });

                loadInvoice();
            })
            .catch(err => {
                Swal.fire({
                    icon: "error",
                    text: get(err, "response.data.message", null) || err.message,
                    timer: 1500,
                });

                loadInvoice();
            });
    };

    // when a value change is requested, let's first make sure that the value is actually changing from the current value
    // if not, no point showing the modal window asking for confirmation, right?
    //
    const handleChangeRequest =
        field =>
        (e, { value }) => {
            const previousValue = invoice[field],
                newValue = value,
                changeData = { field, previousValue, newValue },
                changedValues = isChangeNeeded(changeData);

            if (!changedValues) return null;

            setConfirmChange(changeData);
        };

    useEffect(() => {
        setLoading(true);
        loadInvoice();
    }, []);

    if (!isLoading && error && !invoice) {
        return <Message content={error} header="Sorry, something went wrong!" />;
    }

    const hasEmailFeature = isFeatureActive(company, EmailFeatureDetails);
    const showFintechHistory =
        isFeatureActive(company, FintechFeature) && invoice.id && invoice.customer?.fintech_id;

    return (
        <>
            <Grid className="invoice-view-page">
                <TopNav
                    headerIcon="newspaper"
                    subheader="Details of your invoice"
                    headerContent={`Invoice #${number}`}
                    extraButtons={() => (
                        <>
                            <Menu.Item
                                name="All Invoices"
                                data-cy="all_invoices"
                                icon="chevron left"
                                to="/invoice"
                                as={Link}
                            />
                            {hasMPowerFeature && (
                                <Menu.Item
                                    name="Sync with MPower"
                                    icon="sync"
                                    onClick={syncInvoice}
                                />
                            )}
                            {canEdit && (
                                <Menu.Item
                                    as={Link}
                                    name="Edit"
                                    data-cy="edit_invoice"
                                    icon="pencil"
                                    to={`/invoice/${number}/edit`}
                                />
                            )}
                            {canDelete && (
                                <Menu.Item
                                    data-cy="delete_invoice"
                                    name="Delete"
                                    disabled={isDeleting}
                                    icon={{
                                        name: isDeleting ? "spinner" : "trash",
                                        loading: isDeleting,
                                    }}
                                    onClick={() => setDeleteConfirming(true)}
                                />
                            )}
                            <Menu.Item
                                data-cy="invoice_pdf_button"
                                name="Pdf"
                                onClick={handlePdfGeneration}
                                disabled={!invoice || generatingPdfUrl}
                                icon={{
                                    name: generatingPdfUrl ? "spinner" : "file",
                                    loading: generatingPdfUrl,
                                }}
                            />
                            <Menu.Item
                                data-cy="pick_list_button"
                                name="Pick List"
                                onClick={handlePdfLocationsGeneration}
                                disabled={!invoice || generatingPdfLocationsUrl}
                                icon={{
                                    name: generatingPdfLocationsUrl ? "spinner" : "file",
                                    loading: generatingPdfLocationsUrl,
                                }}
                            />
                        </>
                    )}
                />

                <Confirm
                    header="Confirm Status Change"
                    size="tiny"
                    open={!!invoiceStatus}
                    onConfirm={onConfirmStatusChange}
                    onCancel={() => setInvoiceStatus(null)}
                    confirmButton={{ content: "Yes, Change It", loading: isSaving }}
                    content={
                        !!invoiceStatus && (
                            <Modal.Content>
                                Do you want to change the invoice status to{" "}
                                <b>{statusMapping[invoiceStatus.newStatus]?.text}</b>?
                            </Modal.Content>
                        )
                    }
                />

                <Confirm
                    data-cy="confirm_change_modal"
                    size="tiny"
                    open={!!confirmChange}
                    onConfirm={saveChange}
                    onCancel={() => setConfirmChange(null)}
                    confirmButton={{
                        "data-cy": "confirm_change_modal_confirm_button",
                        content: "Yes, Change It",
                        loading: isSaving,
                    }}
                    content={!!confirmChange && <ConfirmContent {...confirmChange} />}
                />

                <Confirm
                    size="tiny"
                    open={!!isDeleteConfirming}
                    onConfirm={handleDelete}
                    cancelButton="No, Keep It"
                    header="Are you absolutely sure?"
                    confirmButton={{ negative: true, content: "Yes, Delete It" }}
                    onCancel={() => setDeleteConfirming(false)}
                    content="This action is irreversible. You will lose all the data associated with this invoice once deleted."
                />

                <Grid.Column mobile={16}>
                    <InvoiceComponentView
                        invoice={invoice}
                        company={company}
                        isLoading={isLoading || isSaving}
                        onStatusChange={(e, { value }) => handleStatusChangeRequest(value)}
                        onDueDateChange={handleChangeRequest("due_at")}
                        onDateChange={handleChangeRequest("invoiced_at")}
                    />
                </Grid.Column>

                {hasPaymentFeature && (
                    <Grid.Column mobile={16}>
                        <InvoicePaymentListContainer
                            invoice={invoice}
                            handlePaymentChange={handlePaymentChange}
                            user={user}
                        />
                    </Grid.Column>
                )}

                <Grid.Row>
                    <Grid.Column mobile={16} computer={showFintechHistory ? 10 : 16}>
                        <DocumentContainer invoiceId={invoice.id} />
                    </Grid.Column>
                    {showFintechHistory && (
                        <Grid.Column mobile={16} computer={6}>
                            <FintechComponentSyncHistory invoiceId={invoice.id} />
                        </Grid.Column>
                    )}
                </Grid.Row>

                {hasEmailFeature && (
                    <Grid.Column mobile={16}>
                        <Table fixed className="no-print">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Email log</Table.HeaderCell>
                                    <Table.HeaderCell colSpan="2" textAlign="right">
                                        <Button
                                            component={Link}
                                            basic
                                            positive
                                            size="tiny"
                                            icon="envelope"
                                            floated="right"
                                            labelPosition="left"
                                            className="no-print"
                                            content="Send Email"
                                            onClick={() => setEmailModalOpen(true)}
                                        />
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Emailed To</Table.HeaderCell>
                                    <Table.HeaderCell>Notes</Table.HeaderCell>
                                    <Table.HeaderCell>Sent at</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {invoice?.emails?.length ? (
                                    invoice.emails.reverse().map(email => (
                                        <Table.Row key="email">
                                            <Table.Cell>{email.emailed_to}</Table.Cell>
                                            <Table.Cell>{email.notes}</Table.Cell>
                                            <Table.Cell>
                                                {format(new Date(email.created_at), DateTimeFormat)}
                                            </Table.Cell>
                                        </Table.Row>
                                    ))
                                ) : (
                                    <Table.Row>
                                        <Table.Cell>
                                            You haven't sent this invoice to anyone yet
                                        </Table.Cell>
                                    </Table.Row>
                                )}
                            </Table.Body>
                        </Table>
                    </Grid.Column>
                )}
                <Grid.Column mobile={16}>
                    <OrderNotesComponent order={invoice?.orders?.[0]} />
                </Grid.Column>
            </Grid>

            <InvoiceEmailModal
                invoice={invoice}
                open={emailModalOpen}
                setOpen={setEmailModalOpen}
            />
        </>
    );
};

export default InvoicePageView;
